import Immutable from 'immutable';
import mapValues from 'lodash/mapValues';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';

import ProgressBar from 'app/components/shared/ProgressBar';
import { getRelationships } from 'app/actions/RelationshipsActions';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import ErrorPage from 'app/components/ErrorPage';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { mapSearchBibAvailabilityToDisplayAvailability } from 'app/helpers/displayBib';
import { mapBrowseConfigElementsToRows } from 'app/helpers/browse/rows';
import useFeature from 'app/hooks/useFeature';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectBrowseConfig, selectIsFetching } from 'app/selectors/BrowseSelector';
import { selectBibEntities, selectListEntities, selectUserEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import BrowsePage from './BrowsePage';

const BrowsePageContainer = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const currentLibrary = useSelector(selectCurrentLibrary);
  const currentUser = useSelector(selectCurrentUser);
  const config = useSelector(selectBrowseConfig) ?? Immutable.Map();
  const searchBibByMetadataId = useSelector(selectBibEntities);
  const isFetching = useSelector(selectIsFetching);
  const listsById = useSelector(selectListEntities);
  const usersById = useSelector(selectUserEntities);
  const isCoreV2BrowsePageEnabled = useFeature(TOGGLZ_FEATURES.CORE_V2_BROWSE_PAGE);

  const pageTitle = intl.formatMessage({ defaultMessage: 'Browse', id: 'browse' });

  useEffect(() => {
    if (currentUser?.isEmpty() ?? true) {
      return;
    }

    const listCreatorIds = listsById.valueSeq().map(list => list.get('owner'));

    dispatch(getRelationships({ userId: currentUser.get('id'), userIds: listCreatorIds }));
  }, [currentUser, dispatch, listsById]);

  if (!isCoreV2BrowsePageEnabled) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const displayAvailabilitByMetadataId = mapValues(searchBibByMetadataId?.toJS() ?? {}, searchBib => {
    const { availability } = searchBib;
    return mapSearchBibAvailabilityToDisplayAvailability(availability);
  });

  const rows = mapBrowseConfigElementsToRows(config.get('elements', Immutable.List())?.toJS(), {
    hasHistoryRoutingForBibTitles: false,
    hasHistoryRoutingForNavLinks: false,
    listById: listsById?.toJS(),
    searchBibByMetadataId: searchBibByMetadataId?.toJS(),
    userById: usersById?.toJS()
  });

  return (
    <>
      <DocumentHeadContainer
        description={intl.formatMessage(
          { id: 'browse_and_discover_description' },
          { libraryName: currentLibrary?.get('fullName') }
        )}
        pageTitle={pageTitle}
      />
      <ProgressBar active={isFetching} />
      <BackToLinkManager pageId="browse" />
      <BrowsePage displayAvailabilitByMetadataId={displayAvailabilitByMetadataId} pageTitle={pageTitle} rows={rows} />
    </>
  );
};

export default BrowsePageContainer;
